'use client';

import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold, faItalic, faUnderline, faStrikethrough, faCode,
  faHeading, faListUl, faQuoteLeft, faMinus, faLink, faImage,
  faUndo, faRedo, faBroom,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import styles from './EditorToolbar.module.css';

function formatTiptapDocument(doc) {
  if (!doc || !Array.isArray(doc.content)) return doc;

  let firstHeadingUsed = false;

  const cleanContent = (nodes = []) =>
    nodes
      .map((node) => {
        if (!node || typeof node !== 'object') return node;

        if (node.type === 'paragraph') {
          const hasText = Array.isArray(node.content) && node.content.some((c) => c && c.type === 'text' && (c.text || '').trim() !== '');
          if (!hasText) return null;
        }

        if (node.type === 'heading') {
          if (!firstHeadingUsed) {
            firstHeadingUsed = true;
            return {
              ...node,
              attrs: { ...node.attrs, level: 1 },
            };
          }

          return {
            ...node,
            attrs: { ...node.attrs, level: Math.min(node.attrs?.level || 2, 3) },
          };
        }

        if (Array.isArray(node.content) && node.content.length > 0) {
          return {
            ...node,
            content: cleanContent(node.content),
          };
        }

        return node;
      })
      .filter(Boolean);

  return {
    ...doc,
    content: cleanContent(doc.content),
  };
}

export default function EditorToolbar({
  editor,
  isEditable = true,

  onRegisterPendingImage = () =>
    `p-${Math.random().toString(36).slice(2, 9)}`,
}) {
  const imageInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [, forceRender] = useState({});
  const renderKey = useRef({});

  useEffect(() => {
    if (!editor) return;
    const rerender = () => {
      renderKey.current = {};
      forceRender({});
    };
    editor.on('update', rerender);
    editor.on('selectionUpdate', rerender);
    editor.on('transaction', rerender);
    return () => {
      editor.off('update', rerender);
      editor.off('selectionUpdate', rerender);
      editor.off('transaction', rerender);
    };
  }, [editor]);

  if (!editor) return null;

  const preventMouseDown = (e) => e.preventDefault();

  const run = (fn) => {
    requestAnimationFrame(() => {
      try {

        if (editor && editor.chain) {
          editor.chain().focus().run();
        }

        fn();
      } catch (e) {
        console.error(e);
      }
    });
  };

  const isMarkActive = (mark) =>

    editor.isActive(mark) ||

    (editor.state?.storedMarks || []).some((m) => m?.type?.name === mark);

  const isHeadingActive = (level) =>
    editor.isActive('heading', { level });

  const ToolButton = ({
    icon,
    label,
    onClick,
    active = false,
    disabled = false,
    title,
    attach,
  }) => (
    <button
      type="button"
      onMouseDown={preventMouseDown}
      onClick={() => run(onClick)}
      disabled={!isEditable || disabled}
      title={title || label}
      aria-pressed={active}
      className={`${styles.toolButton} ${active ? styles.active : ''}`}
    >
      <FontAwesomeIcon icon={icon} />
      {attach && <small>{attach}</small>}
    </button>
  );

  const openImagePicker = () => imageInputRef.current?.click();

  const handleImageFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image too large (max 10MB)');
      return;
    }

    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = () => {
      const previewDataUrl = reader.result;

      const pendingId = onRegisterPendingImage(file);

      editor
        .chain()
        .focus()
        .setImage({
          src: previewDataUrl,
          alt: file.name || '',
          pending_id: pendingId,
        })
        .createParagraphNear()
        .run();

      setIsProcessing(false);
    };

    reader.onerror = () => {
      toast.error('Failed to read image');
      setIsProcessing(false);
    };

    reader.readAsDataURL(file);
  };

  const formatDocument = () => {
    if (!editor) return;
    try {
      const json = editor.getJSON();
      const formatted = formatTiptapDocument(json);

      editor.commands.setContent(formatted, false);
      toast.success('Document formatted');
    } catch (err) {
      console.error('formatDocument error:', err);
      toast.error('Failed to format document');
    }
  };

  return (
    <div className={styles.toolbar} role="toolbar">
      <div className={styles.group}>
        <ToolButton icon={faBold} label="Bold" active={isMarkActive('bold')} onClick={() => editor.chain().toggleBold().run()} />
        <ToolButton icon={faItalic} label="Italic" active={isMarkActive('italic')} onClick={() => editor.chain().toggleItalic().run()} />
        <ToolButton icon={faUnderline} label="Underline" active={isMarkActive('underline')} onClick={() => editor.chain().toggleUnderline().run()} />
        <ToolButton icon={faStrikethrough} label="Strike" active={isMarkActive('strike')} onClick={() => editor.chain().toggleStrike().run()} />
        <ToolButton icon={faCode} label="Code" active={isMarkActive('code')} onClick={() => editor.chain().toggleCode().run()} />
      </div>

      <div className={styles.divider} />

      <div className={styles.group}>
        {[1, 2, 3].map((lvl) => (
          <ToolButton
            key={lvl}
            icon={faHeading}
            label={`H${lvl}`}
            attach={lvl}
            active={isHeadingActive(lvl)}
            onClick={() => editor.chain().toggleHeading({ level: lvl }).run()}
          />
        ))}
      </div>

      <div className={styles.divider} />

      <div className={styles.group}>
        <ToolButton icon={faListUl} label="List" active={editor.isActive('bulletList')} onClick={() => editor.chain().toggleBulletList().run()} />
        <ToolButton icon={faQuoteLeft} label="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().toggleBlockquote().run()} />
        <ToolButton icon={faMinus} label="Divider" onClick={() => editor.chain().setHorizontalRule().run()} />
      </div>

      <div className={styles.divider} />

      <div className={styles.group}>
        <ToolButton
          icon={faLink}
          label="Link"
          active={editor.isActive('link')}
          onClick={() => {
            const url = prompt('Enter URL');
            if (url) editor.chain().extendMarkRange('link').setLink({ href: url }).run();
          }}
        />

        <ToolButton
          icon={faImage}
          label="Image"
          title="Insert image"
          onClick={openImagePicker}
          disabled={isProcessing}
        />

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleImageFileSelect}
        />
      </div>

      <div className={styles.divider} />

      <div className={styles.group}>
        <ToolButton icon={faUndo} label="Undo" onClick={() => editor.chain().undo().run()} />
        <ToolButton icon={faRedo} label="Redo" onClick={() => editor.chain().redo().run()} />
      </div>

      <div className={styles.divider} />

      <div className={styles.group}>
        <ToolButton icon={faBroom} label="Format" title="Format Document" onClick={formatDocument} />
      </div>

      {isProcessing && (
        <div className={styles.uploadingIndicator}>
          <span>Processing…</span>
        </div>
      )}
    </div>
  );
}

